import { useState, useEffect } from 'react';
import { Trash2, AlertTriangle, CreditCard } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';

interface Company {
  id: string;
  name: string;
  slug: string;
  status: string;
  user_count?: number;
}

interface DeleteCompanyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
  onConfirm: (companyId: string, confirmationName: string, cancelSubscriptions?: boolean) => Promise<void>;
  isLoading?: boolean;
}

export function DeleteCompanyModal({
  open,
  onOpenChange,
  company,
  onConfirm,
  isLoading = false,
}: DeleteCompanyModalProps) {
  const [confirmationName, setConfirmationName] = useState('');
  const [isConfirmationValid, setIsConfirmationValid] = useState(false);
  const [cancelSubscriptions, setCancelSubscriptions] = useState(false);
  const [hasActiveSubscriptions, setHasActiveSubscriptions] = useState(false);
  const [activeSubscriptionCount, setActiveSubscriptionCount] = useState(0);

  useEffect(() => {
    const checkActiveSubscriptions = async () => {
      if (!open || !company) {
        setHasActiveSubscriptions(false);
        setActiveSubscriptionCount(0);
        return;
      }

      try {
        const [cspResult, mpResult] = await Promise.all([
          supabase
            .from('company_system_plans')
            .select('id', { count: 'exact', head: true })
            .eq('company_id', company.id)
            .in('status', ['active', 'pending']),
          supabase
            .from('mp_subscriptions')
            .select('id', { count: 'exact', head: true })
            .eq('company_id', company.id)
            .in('status', ['active', 'authorized'])
        ]);

        const totalCount = (cspResult.count || 0) + (mpResult.count || 0);
        setActiveSubscriptionCount(totalCount);
        setHasActiveSubscriptions(totalCount > 0);
        
        if (totalCount > 0) {
          setCancelSubscriptions(true);
        }
      } catch (error) {
        console.error('Error checking subscriptions:', error);
        setHasActiveSubscriptions(false);
        setActiveSubscriptionCount(0);
      }
    };

    checkActiveSubscriptions();
  }, [open, company]);

  const handleConfirmationChange = (value: string) => {
    setConfirmationName(value);
    setIsConfirmationValid(
      value.toLowerCase().trim() === company?.name.toLowerCase().trim()
    );
  };

  const handleConfirm = async () => {
    if (!company || !isConfirmationValid) return;
    
    try {
      await onConfirm(company.id, confirmationName, cancelSubscriptions);
      setConfirmationName('');
      setIsConfirmationValid(false);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const handleCancel = () => {
    setConfirmationName('');
    setIsConfirmationValid(false);
    setCancelSubscriptions(false);
    setHasActiveSubscriptions(false);
    setActiveSubscriptionCount(0);
    onOpenChange(false);
  };

  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Excluir Empresa
          </DialogTitle>
          <DialogDescription>
            Esta ação é <strong>irreversível</strong> e excluirá permanentemente a empresa e todos os dados relacionados.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Atenção:</strong> Esta operação irá excluir:
              <ul className="mt-2 ml-4 list-disc space-y-1">
                <li>A empresa <strong>{company.name}</strong></li>
                <li>Todos os usuários vinculados apenas a esta empresa</li>
                <li>Todos os dados de frota, documentos e relatórios</li>
                <li>Histórico de pagamentos e assinaturas</li>
              </ul>
            </AlertDescription>
          </Alert>

          <div className="bg-muted p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="font-medium">Empresa:</span>
              <span>{company.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Slug:</span>
              <span className="font-mono text-sm">{company.slug}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Status:</span>
              <Badge variant={company.status === 'active' ? 'default' : 'secondary'}>
                {company.status}
              </Badge>
            </div>
            {company.user_count !== undefined && (
              <div className="flex justify-between">
                <span className="font-medium">Usuários:</span>
                <span>{company.user_count} usuário(s)</span>
              </div>
            )}
          </div>

          {hasActiveSubscriptions && (
            <Alert className="border-orange-200 bg-orange-50">
              <CreditCard className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                <strong>Assinaturas ativas encontradas: {activeSubscriptionCount}</strong>
                <br />
                Esta empresa possui assinaturas ativas. Elas serão canceladas automaticamente 
                antes da exclusão (pré-aprovações MP e planos do sistema serão removidos).
              </AlertDescription>
            </Alert>
          )}

          {hasActiveSubscriptions && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="cancel-subscriptions"
                checked={cancelSubscriptions}
                onCheckedChange={(checked) => setCancelSubscriptions(checked as boolean)}
              />
              <Label htmlFor="cancel-subscriptions" className="text-sm font-medium">
                Cancelar todas as assinaturas ativas automaticamente
              </Label>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="confirmation">
              Para confirmar, digite o nome da empresa: <strong>{company.name}</strong>
            </Label>
            <Input
              id="confirmation"
              value={confirmationName}
              onChange={(e) => handleConfirmationChange(e.target.value)}
              placeholder="Digite o nome da empresa"
              disabled={isLoading}
              className={isConfirmationValid ? 'border-success' : ''}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isConfirmationValid || isLoading}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2" />
                Excluindo...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir Empresa
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}